/**
 * Formatting shared by the analytics page and its drill-down panels.
 *
 * Kept out of the components so the two never drift: a duration rendered as
 * "2h 14m" in the summary and "134 minutes" in the detail reads as two
 * different measurements of two different things.
 */

/** Compact duration: 0m, 47m, 2h, 2h 14m. */
export function fmtDuration(ms) {
  if (!ms || ms < 0) return '0m';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Thousands-separated integer, tolerant of null. */
export function fmtNumber(n) {
  return (n || 0).toLocaleString();
}

/** "4 Sep, 14:32" — date and time, because a session without a clock is useless. */
export function fmtDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

/** Just the clock, for the second endpoint of a same-day range. */
export function fmtTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * A session's span. Collapses to one date when start and end share a day,
 * which is the overwhelmingly common case and halves the width of the column.
 */
export function fmtSpan(start, end) {
  if (!start) return '—';
  const a = new Date(start);
  const b = end ? new Date(end) : null;
  if (!b || Number.isNaN(b.getTime())) return fmtDateTime(a);
  const sameDay = a.toDateString() === b.toDateString();
  return sameDay ? `${fmtDateTime(a)} – ${fmtTime(b)}` : `${fmtDateTime(a)} – ${fmtDateTime(b)}`;
}

/**
 * How a person is labelled everywhere in analytics.
 *
 * An id alone is not actionable — the whole point of naming students is that
 * you can go and talk to one. When the account has been deleted there is no
 * name to show, and saying so beats rendering an empty cell that reads as a
 * loading bug.
 */
export function displayName(person) {
  if (!person) return 'Unknown';
  return person.name || `Student ${person.studentId}` || 'Unknown';
}

/** Monospaced, tabular figures — for anything that lines up in a column. */
export const num = { fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' };
